import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import {
  FILTER_DELIVERY,
  getFiltersMap,
  RangeFacetLocal,
  TextFacetLocal,
  TextFilterItem,
} from 'components/Filter/Filter.types';
import { SortData } from 'components/Sort/Sort.types';
import { TIRE } from 'domains/catalog/Catalog.types';
import {
  getTireCatalogFilters,
  getTireSearchResult,
  resetTireFilters,
  resetTireTextFilter,
  setTireRangeFilter,
  setTireTextFilter,
  setTireTextFilters,
} from 'domains/tires/Tire.store';
import { TireSortReference } from 'domains/tires/Tire.types';
import { CompactView, getCatalogReferenceView, SparePartsViewType } from 'domains/user';
import { LOADING, useLarge } from 'utils';
import { getFilteredTires, getUpdatedTireTextFilters } from './TireFilterUtils';
import { getSomeStockMissingFirstLoad, splitTiresByExactMatch } from './tireReferencesAvailability';
import {
  getFilteredInitialTextFilters,
  getNextFilterSectionState,
  shouldInitializeTextFilters,
} from './tireReferencesFilters';
import { createPricesFacet, getPricesRange } from './tireReferencesPricing';
import { useTireReferencesDataState } from './useTireReferencesDataState';

import { TireReferencesState } from './useTireReferencesState.types';

const MOBILE_MARGIN = 15;

export const useTireReferencesState = ({
  searchParamsBase64,
  sparePartsView,
  setNumOfProducts,
  initialTextFilters,
  resetInitialTextFilters,
  sort,
}: {
  searchParamsBase64: string;
  sparePartsView: SparePartsViewType;
  setNumOfProducts: (num: number) => void;
  initialTextFilters?: TextFilterItem[];
  resetInitialTextFilters?: () => void;
  sort: SortData<TireSortReference>[];
}): TireReferencesState => {
  const { t } = useTranslation();
  const large = useLarge();
  const dispatch = useDispatch();

  const filters = useSelector(getTireCatalogFilters);
  const catalogTires = useSelector((state: RootState) => getTireSearchResult(state, { searchParamsBase64 }));
  const referenceView = useSelector(getCatalogReferenceView);
  const compact = referenceView === CompactView;

  const {
    allTires,
    refsForStocks,
    stocks,
    prices,
    brandCategoriesMap,
    stockStatuses,
    hasPendingStockStatuses,
    hasUnresolvedStockEntries,
    somePriceMissing,
    someStockLoading,
    availableTires,
    priceForAvailable,
  } = useTireReferencesDataState(catalogTires);
  const [showPriceless, setShowPriceless] = useState<boolean>(false);
  const [someStockMissingFirstLoad, setSomeStockMissingFirstLoad] = useState(true);
  const [isFilterSectionReady, setIsFilterSectionReady] = useState(false);

  const [textFilters, setTextFilters] = useState<TextFacetLocal[]>(catalogTires?.data?.textFilters ?? []);
  const textFiltersWithCurrentCounts = useMemo(
    () => getUpdatedTireTextFilters(textFilters, stocks, availableTires, brandCategoriesMap, t),
    [textFilters, stocks, availableTires, brandCategoriesMap, t],
  );
  const pricesRange: [number, number] | undefined = useMemo(() => getPricesRange(priceForAvailable, sparePartsView), [
    priceForAvailable,
    sparePartsView,
  ]);

  const pricesFacet = createPricesFacet(pricesRange, availableTires.length);
  const [rangeFilters, setRangeFilters] = useState<RangeFacetLocal[]>([pricesFacet]);

  const handleSetTextFilters = useCallback(
    (id: string, item: string) => {
      dispatch(setTireTextFilter({ id, item }));
    },
    [dispatch],
  );
  const handleSetRangeFilters = useCallback(
    (id: string, range?: { min: number; max: number }) => {
      if (!range) {
        return;
      }
      dispatch(setTireRangeFilter({ id, range }));
    },
    [dispatch],
  );
  const handleResetFilters = useCallback(() => {
    dispatch(resetTireFilters());
  }, [dispatch]);

  const filteredInitialTextFilters: TextFilterItem[] = useMemo(
    () => getFilteredInitialTextFilters(initialTextFilters, availableTires),
    [availableTires, initialTextFilters],
  );

  useEffect(() => {
    setSomeStockMissingFirstLoad(
      getSomeStockMissingFirstLoad(refsForStocks.length, hasUnresolvedStockEntries, stockStatuses),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockStatuses, refsForStocks.length, hasUnresolvedStockEntries]);

  useEffect(() => {
    setSomeStockMissingFirstLoad(true);
    setIsFilterSectionReady(false);
  }, [searchParamsBase64]);

  const filteredTires = useMemo(
    () =>
      getFilteredTires({
        availableTires,
        filters,
        prices,
        sparePartsView,
        showPriceless,
        stocks,
        brandCategoriesMap,
        t,
      }),
    [availableTires, filters, prices, stocks, t, brandCategoriesMap, sparePartsView, showPriceless],
  );

  const { exactMatchTires, nonExactMatchTires } = useMemo(() => splitTiresByExactMatch(filteredTires), [filteredTires]);

  const initialTireTextFiltersJson = JSON.stringify(catalogTires?.data?.textFilters);
  const textFiltersJson = JSON.stringify(textFilters);
  useEffect(() => {
    const { nextTextFilters, shouldResetDeliveryFilter, updatedRangeFilters } = getNextFilterSectionState({
      initialTireTextFilters: catalogTires?.data?.textFilters ?? [],
      deliverySelection: filters.textFilters.get(FILTER_DELIVERY),
      stocks,
      availableTires,
      brandCategoriesMap,
      t,
      rangeFilters,
      filteredTires,
    });

    if (shouldResetDeliveryFilter) {
      dispatch(resetTireTextFilter({ filterOption: FILTER_DELIVERY }));
    }
    setTextFilters(nextTextFilters);
    setRangeFilters(updatedRangeFilters);
    setIsFilterSectionReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textFiltersJson, initialTireTextFiltersJson]);

  useEffect(() => {
    if (!shouldInitializeTextFilters(someStockMissingFirstLoad, someStockLoading, initialTextFilters)) {
      return;
    }
    dispatch(setTireTextFilters(filteredInitialTextFilters));
    resetInitialTextFilters?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, someStockMissingFirstLoad, someStockLoading, resetInitialTextFilters]);

  const priceForAvailableJson = JSON.stringify(priceForAvailable);
  useEffect(() => {
    setRangeFilters([pricesFacet]);
    // eslint-disable-next-line
  }, [somePriceMissing, priceForAvailableJson]);

  useEffect(() => {
    setNumOfProducts(filteredTires.length);
    // eslint-disable-next-line
  }, [filteredTires.length]);

  const isFilterLoading = [
    catalogTires?.searchStatus === LOADING,
    hasPendingStockStatuses,
    hasUnresolvedStockEntries,
    someStockLoading,
    someStockMissingFirstLoad,
    !isFilterSectionReady,
  ].some(Boolean);

  return {
    direction: large ? 'row' : 'column',
    margin: large ? 0 : MOBILE_MARGIN,
    filtersMapType: TIRE,
    filtersMap: getFiltersMap(t),
    handleSetTextFilters,
    handleSetRangeFilters,
    handleResetFilters,
    pricesRange,
    large,
    catalogTires,
    allTires,
    availableTires,
    filteredTires,
    exactMatchTires,
    nonExactMatchTires,
    filters,
    textFiltersWithCurrentCounts,
    rangeFilters,
    isFilterLoading,
    showPriceless,
    setShowPriceless,
    compact,
    t,
    sort,
    sparePartsView,
  };
};
